import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  RefreshCw, Eye, XCircle, Loader2, WifiOff, CalendarClock, X,
  Building2, Tag, IndianRupee, Activity, Clock, History, Ban, RotateCcw, Repeat,
  ChevronsUpDown, ChevronUp, ChevronDown,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Modal from "@/components/dashboard/Modal";
import LoadError from "@/components/dashboard/LoadError";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { useApiData } from "@/hooks/useApiData";
import { listSubscriptions, cancelCompanySubscription, listAuditLog } from "@/api/admin";
import { COMPANIES } from "@/data/mock";
import { toastError, toastSuccess } from "@/lib/toast";
import { formatDate, formatDateTime } from "@/lib/dates";
import { usePlans } from "@/hooks/usePlans";

// Offline fixture only. Everything on screen reads the LIVE catalog (usePlans):
// a frozen map here meant renaming a plan in /admin/plans left this page showing
// the old name forever, and the plan filter offered a fixed set of ids.
const DEMO_PLAN_PRICE = { free_trial: 0, starter: 3999, growth: 9999, custom: 0 };
const DEMO_PLAN_NAME = { free_trial: "Free Trial", starter: "Starter Plan", growth: "Growth Plan", custom: "Custom Plan" };

const STATUS_CHIP = {
  active: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
  trialing: "bg-sky-500/15 text-sky-600 dark:text-sky-300",
  past_due: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  canceled: "bg-slate-200/80 text-slate-500 dark:bg-white/10 dark:text-slate-400",
};
const PLAN_CHIP = {
  growth: "bg-brand-500/15 text-brand-600 dark:text-brand-300",
  custom: "bg-violet-500/15 text-violet-600 dark:text-violet-300",
  starter: "bg-sky-500/15 text-sky-600 dark:text-sky-300",
  free_trial: "bg-slate-200/80 text-slate-500 dark:bg-white/10 dark:text-slate-400",
};

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
function slugify(n) { return (n || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }
// A bare "YYYY-MM-DD" (the date picker's value) is parsed as UTC midnight, so it
// must be rendered in UTC too — otherwise the chip and the picker beside it show
// different days in any zone west of Greenwich. Full timestamps stay local.
// formatDate reads a bare YYYY-MM-DD off the string, so the UTC guard this
// used to need is now part of the shared helper.
const fmtDate = (v) => formatDate(v);
const fmtDateTime = (v) => formatDateTime(v);
function relTime(v) {
  if (!v) return "—";
  const t = new Date(v).getTime();
  if (Number.isNaN(t)) return "—";
  const s = (Date.now() - t) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const DEMO_SUBS = COMPANIES.map((c, i) => ({
  company_id: c.id, company_name: c.name, slug: slugify(c.name), is_active: c.is_active,
  owner_email: `owner@${slugify(c.name)}.com`, plan_id: c.plan_id, plan_name: DEMO_PLAN_NAME[c.plan_id],
  status: c.status, amount: DEMO_PLAN_PRICE[c.plan_id],
  created_at: `2026-0${5 - (i % 2)}-2${(i % 8)}T09:57:00Z`,
  current_period_end: `2026-07-${10 + i}T00:00:00Z`,
}));

const PAGE_SIZE = 10;
const STATUS_FILTERS = ["all", "active", "trialing", "past_due", "canceled"];
// The only statuses the cancel endpoint accepts. A tenant that never bought or
// started a trial has no subscription row at all and reports "none", so the red
// icon used to be offered on rows where it could only answer 404.
const CANCELABLE = ["active", "trialing"];
// created_at/current_period_end are ISO timestamp strings — a plain
// localeCompare still sorts them correctly, but a real Date comparison
// doesn't depend on every row using the exact same string format.
const DATE_SORT_KEYS = ["created_at", "current_period_end"];

export default function Subscriptions() {
  const navigate = useNavigate();
  const { data, loading, demo, error, reload } = useApiData(listSubscriptions, DEMO_SUBS);
  // The live catalog drives the plan filter.
  const { plans: planOptions } = usePlans();
  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expBy, setExpBy] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  // null = unsorted (original, newest-first order from the API).
  const [sort, setSort] = useState(null);

  useEffect(() => { setPage(1); }, [search, planFilter, statusFilter, expBy]);

  function toggleSort(key) {
    setSort((s) => {
      if (!s || s.key !== key) return { key, dir: "asc" };
      if (s.dir === "asc") return { key, dir: "desc" };
      return null; // third click: back to unsorted
    });
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((s) => {
      if (statusFilter !== "all" && String(s.status).toLowerCase() !== statusFilter) return false;
      if (planFilter !== "all" && s.plan_id !== planFilter) return false;
      if (q && ![s.company_name, s.owner_email, s.plan_name].some((v) => String(v || "").toLowerCase().includes(q))) return false;
      if (expBy) {
        if (!s.current_period_end) return false;
        // current_period_end is a UTC timestamp, so the cutoff must be UTC end
        // of day — a local-zone cutoff hides renewals the operator is hunting.
        if (new Date(s.current_period_end) > new Date(`${expBy}T23:59:59.999Z`)) return false;
      }
      return true;
    });
  }, [rows, search, planFilter, statusFilter, expBy]);

  const hasActiveFilters = !!search || planFilter !== "all" || statusFilter !== "all" || !!expBy;

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const { key, dir } = sort;
    const mul = dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      const aEmpty = av === null || av === undefined || av === "";
      const bEmpty = bv === null || bv === undefined || bv === "";
      if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : (aEmpty ? 1 : -1);
      if (typeof av === "number" && typeof bv === "number") return mul * (av - bv);
      if (DATE_SORT_KEYS.includes(key)) return mul * (new Date(av).getTime() - new Date(bv).getTime());
      return mul * String(av).localeCompare(String(bv));
    });
  }, [filtered, sort]);

  const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCompany = (s) => navigate(`/admin/companies/${s.company_id}`);

  async function doCancel() {
    if (!cancelConfirm) return;
    setBusy(true);
    try {
      await cancelCompanySubscription(cancelConfirm.company_id);
    } catch (e) {
      setBusy(false);
      toastError(e.message || "Couldn't cancel the subscription.");
      return;
    }
    setBusy(false); setCancelConfirm(null);
    toastSuccess(`${cancelConfirm.company_name}'s subscription canceled.`);
    reload();
  }

  return (
    <>
      <PageHeader
        title="Subscriptions"
        subtitle={`${rows.length} registered ${rows.length === 1 ? "company" : "companies"}`}
        actions={
          <>
            {/* A bare date picker with only a hover title gave no clue what it
                filtered on until you tried it — a visible label beats relying
                on a tooltip nobody hovers long enough to see. */}
            <div className="flex items-center gap-1.5">
              <label htmlFor="exp-by-filter" className="text-xs font-medium text-slate-400">Expires by</label>
              <div className="relative">
                <CalendarClock size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  id="exp-by-filter"
                  type="date"
                  value={expBy}
                  onChange={(e) => setExpBy(e.target.value)}
                  title="Show subscriptions expiring on or before this date"
                  className="h-9 rounded-xl border border-slate-200 bg-white pl-8 pr-2 text-sm text-slate-700 focus:border-brand-400 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                />
                {expBy && (
                  <button type="button" onClick={() => setExpBy("")} title="Clear" className="absolute -right-2 -top-2 grid h-5 w-5 place-items-center rounded-full bg-slate-200 text-slate-600 hover:bg-slate-300 dark:bg-white/10 dark:text-slate-300">
                    <X size={11} />
                  </button>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={reload}><RefreshCw size={16} /> Refresh</Button>
          </>
        }
      />

      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Showing demo data — backend not reachable.
        </div>
      )}

      {/* filters */}
      <div className="card mb-4 space-y-3 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative max-w-sm flex-1">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search company, owner or plan…"
              className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 pr-8 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:bg-white focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-white/10 dark:hover:text-white"
              >
                <X size={13} />
              </button>
            )}
          </div>
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-brand-400 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            {/* From the live catalog. These were hardcoded, so a plan the
                backend no longer serves stayed selectable (matching nothing) and
                a newly added one could not be filtered at all. */}
            <option value="all">All plans</option>
            {(planOptions || []).map((p) => (
              <option key={p.id} value={p.id}>{p.name || p.id}</option>
            ))}
          </select>
          {expBy && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
              <CalendarClock size={13} /> Expiring by {fmtDate(expBy)}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors",
                statusFilter === s ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
              )}
            >
              {s === "all" ? "All" : s === "past_due" ? "Past Due" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          {/* Four independent controls (search, plan, status, expiry-by) had no
              single way to reset all of them together — only the date picker
              had its own tiny "x", so clearing "Growth + Active" meant undoing
              each one by hand. */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => { setSearch(""); setPlanFilter("all"); setStatusFilter("all"); setExpBy(""); }}
              className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium text-slate-500 underline-offset-2 hover:text-brand-600 hover:underline dark:text-slate-400 dark:hover:text-brand-300"
            >
              <X size={12} /> Clear filters
            </button>
          )}
        </div>
      </div>

      <LoadError message={error} onRetry={reload} />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>
      ) : error ? null : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-sm">
              <thead className="bg-slate-50/95 dark:bg-ink-900/95">
                <tr>
                  {[
                    { key: "company_name", label: "Company" },
                    { key: "plan_name", label: "Plan" },
                    { key: "status", label: "Status" },
                    { key: "amount", label: "Amount" },
                    { key: "created_at", label: "Created" },
                    { key: "current_period_end", label: "Period end" },
                    { key: null, label: "Actions" },
                  ].map((col) => (
                    <th key={col.label} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {col.key ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(col.key)}
                          className="inline-flex items-center gap-1 hover:text-slate-800 dark:hover:text-white"
                        >
                          {col.label}
                          {!sort || sort.key !== col.key ? (
                            <ChevronsUpDown size={13} className="opacity-50" />
                          ) : sort.dir === "asc" ? (
                            <ChevronUp size={13} />
                          ) : (
                            <ChevronDown size={13} />
                          )}
                        </button>
                      ) : col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {paged.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-400">No subscriptions match these filters.</td></tr>
                ) : paged.map((s, i) => (
                  <motion.tr key={s.company_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.2) }} className="hover:bg-slate-50 dark:hover:bg-white/5">
                    <td className="px-4 py-3">
                      <button onClick={() => openCompany(s)} className="flex items-center gap-3 text-left">
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-xs font-bold text-white">
                          {(s.company_name || "?").slice(0, 2).toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-medium text-slate-800 hover:text-brand-600 dark:text-white">{s.company_name}</p>
                          <p className="truncate text-xs text-slate-400">{s.owner_email || s.slug}</p>
                        </div>
                      </button>
                    </td>
                    <td className="px-4 py-3"><span className={cn("rounded-md px-2 py-0.5 text-xs font-bold uppercase", PLAN_CHIP[s.plan_id] || PLAN_CHIP.free_trial)}>{(s.plan_id || "free_trial").replace(/_/g, " ")}</span></td>
                    <td className="px-4 py-3">
                      <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize", STATUS_CHIP[String(s.status).toLowerCase()] || STATUS_CHIP.canceled)}>
                        <span className="h-1.5 w-1.5 rounded-full bg-current" /> {s.status}
                      </span>
                    </td>
                    {/* A numeric ₹0 rather than the word "Free" — a text value
                        in a currency column reads oddly next to real amounts
                        and can't be compared/sorted the same way (#54). */}
                    <td className="px-4 py-3 font-semibold text-slate-800 dark:text-white">{inr(s.amount)}</td>
                    {/* An actual date, not just "3d ago" — every other date
                        column here (Period end) shows a real date, and Created
                        was the only one that didn't; the relative time is kept
                        as a secondary hint, not dropped (#55). Reordered before
                        Period end so the two chronological columns read
                        left-to-right in the order they actually happen. */}
                    <td className="px-4 py-3">
                      <p className="text-slate-600 dark:text-slate-300">{fmtDate(s.created_at)}</p>
                      <p className="text-xs text-slate-400">{relTime(s.created_at)}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{fmtDate(s.current_period_end)}</td>
                    <td className="px-4 py-3">
                      {/* "Open company" used to sit here AND inside the View
                          modal (twice, in fact) — three controls for one
                          destination (#56/#57). View's modal is now the one
                          and only way to reach the full company page. */}
                      <div className="flex items-center gap-1.5">
                        <IconBtn title="View" onClick={() => setSelected(s)}><Eye size={15} /></IconBtn>
                        {CANCELABLE.includes(String(s.status).toLowerCase()) ? (
                          <IconBtn title="Cancel" tone="rose" onClick={() => setCancelConfirm(s)}><XCircle size={15} /></IconBtn>
                        ) : String(s.status).toLowerCase() === "canceled" ? (
                          // Un-canceling a subscription IS a plan change (the
                          // only endpoint that flips status back to "active"),
                          // so there is no separate one-click "reactivate" to
                          // wire here — this takes the operator straight to
                          // where that already exists (#59), rather than
                          // leaving a canceled row with no path back at all.
                          <IconBtn title="Reactivate (change plan)" tone="emerald" onClick={() => navigate(`/admin/companies/${s.company_id}?tab=subscription`)}>
                            <RotateCcw size={15} />
                          </IconBtn>
                        ) : null}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 dark:border-white/10">
            <span className="text-xs text-slate-400">Showing {paged.length} of {filtered.length}</span>
            <div className="flex items-center gap-3">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-white/5">‹ Prev</button>
              <span className="text-sm text-slate-500">Page {page} of {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-white/5">Next ›</button>
            </div>
          </div>
        </div>
      )}

      <SubscriptionModal sub={selected} onClose={() => setSelected(null)} onOpenCompany={() => { const s = selected; setSelected(null); openCompany(s); }} />

      <Modal
        open={!!cancelConfirm}
        onClose={() => setCancelConfirm(null)}
        title="Cancel subscription?"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setCancelConfirm(null)}>Keep it</Button>
            <button type="button" disabled={busy} onClick={doCancel} className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-rose-600 px-3.5 text-sm font-semibold text-white hover:bg-rose-500 disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <><XCircle size={15} /> Cancel subscription</>}
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Cancel <strong>{cancelConfirm?.company_name}</strong>&apos;s subscription? Access continues until the current period ends; you can re-activate later by changing the plan.
        </p>
      </Modal>
    </>
  );
}

function IconBtn({ title, onClick, children, tone }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={cn(
        "grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-400 transition-colors dark:border-white/10",
        tone === "rose" ? "hover:border-rose-300 hover:text-rose-600"
          : tone === "emerald" ? "hover:border-emerald-300 hover:text-emerald-600"
          : "hover:border-brand-300 hover:text-brand-600"
      )}
    >
      {children}
    </button>
  );
}

// ---- detail modal (redesigned) --------------------------------------------
const ACTION_LABEL = {
  "COMPANY.CANCEL_SUB": "subscription.cancel",
  "COMPANY.CHANGE_PLAN": "subscription.change_plan",
  "COMPANY.SUSPEND": "company.suspend",
  "COMPANY.UNSUSPEND": "company.reactivate",
  "COMPANY.DELETE_SOFT": "company.soft_delete",
  "COMPANY.DELETE_HARD": "company.hard_delete",
  "SUBSCRIPTION.ACTIVATE": "subscription.activate",
  "SUBSCRIPTION.CANCEL": "subscription.cancel",
};
function actionMeta(a) {
  const label = ACTION_LABEL[a] || String(a || "").toLowerCase();
  if (label.includes("cancel") || label.includes("suspend") || label.includes("delete"))
    return { label, Icon: Ban, tone: "bg-rose-500/10 text-rose-600 dark:text-rose-300" };
  if (label.includes("reactivate") || label.includes("activate"))
    return { label, Icon: RotateCcw, tone: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300" };
  if (label.includes("change") || label.includes("plan"))
    return { label, Icon: Repeat, tone: "bg-brand-500/10 text-brand-600 dark:text-brand-300" };
  return { label, Icon: History, tone: "bg-slate-500/10 text-slate-500 dark:text-slate-300" };
}

function SubscriptionModal({ sub, onClose, onOpenCompany }) {
  const [actions, setActions] = useState(null);
  const [loadingActs, setLoadingActs] = useState(false);
  const [actsError, setActsError] = useState(null);
  const [actsAttempt, setActsAttempt] = useState(0);

  useEffect(() => {
    if (!sub) { setActions(null); setActsError(null); return undefined; }
    let alive = true;
    setLoadingActs(true);
    setActsError(null);
    listAuditLog({ search: sub.company_id, days: 365 })
      .then((d) => { if (alive) setActions(Array.isArray(d) ? d.slice(0, 12) : []); })
      // A failed fetch must not render as "nothing ever happened" — this is the
      // one panel whose job is to show that history.
      .catch((e) => { if (alive) { setActions(null); setActsError(e.message || "Couldn't load admin actions."); } })
      .finally(() => { if (alive) setLoadingActs(false); });
    return () => { alive = false; };
  }, [sub, actsAttempt]);

  const tiles = sub ? [
    { icon: Tag, label: "Plan", value: sub.plan_name || sub.plan_id || "—" },
    { icon: IndianRupee, label: "Amount", value: inr(sub.amount) },
    { icon: Activity, label: "Status", value: <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold capitalize", STATUS_CHIP[String(sub.status).toLowerCase()] || STATUS_CHIP.canceled)}>{sub.status}</span>, raw: true },
    { icon: Clock, label: "Created", value: fmtDateTime(sub.created_at) },
    { icon: CalendarClock, label: "Period end", value: fmtDate(sub.current_period_end) },
  ] : [];

  return (
    <Modal
      open={!!sub}
      onClose={onClose}
      title="Subscription"
      size="lg"
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="outline" size="sm" onClick={onOpenCompany}><Building2 size={15} /> Open company</Button>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
      }
    >
      {sub && (
        <div className="space-y-5">
          {/* company header */}
          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-base font-bold text-white shadow-glow">
              {(sub.company_name || "?").slice(0, 2).toUpperCase()}
            </span>
            <div className="min-w-0">
              <p className="truncate text-lg font-bold text-slate-900 dark:text-white">{sub.company_name}</p>
              <p className="truncate font-mono text-xs text-slate-400">{sub.slug} · {sub.owner_email}</p>
            </div>
          </div>

          {/* detail tiles */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {tiles.map((t) => (
              <div key={t.label} className="rounded-xl border border-slate-100 p-3 dark:border-white/10">
                <div className="flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">
                  <t.icon size={13} /> {t.label}
                </div>
                <div className="mt-1.5 text-sm font-semibold text-slate-800 dark:text-white">{t.value}</div>
              </div>
            ))}
          </div>

          {/* recent admin actions */}
          <div>
            <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white"><History size={15} /> Recent admin actions</h4>
            <LoadError message={actsError} onRetry={() => setActsAttempt((n) => n + 1)} className="mt-3" />
            {loadingActs ? (
              <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 size={20} className="animate-spin" /></div>
            ) : actsError ? null : !actions || actions.length === 0 ? (
              <div className="mt-3 rounded-xl border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400 dark:border-white/10">
                No admin actions recorded yet.
              </div>
            ) : (
              <ul className="mt-3 space-y-2">
                {actions.map((a) => {
                  const m = actionMeta(a.action);
                  return (
                    <li key={a.id} className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-2.5 dark:border-white/5">
                      <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg", m.tone)}><m.Icon size={15} /></span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-mono text-sm font-semibold text-slate-800 dark:text-white">{m.label}</p>
                        <p className="truncate text-xs text-slate-400">{a.actor_email || "system"} · {relTime(a.created_at)}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
